import moment from 'moment-timezone';

import { getAmountTenderedByGiftCard } from 'common/helpers/miscHelper';

export const calculateStep = (cart, settings) => {
  let step = 'customer';
  if (cart.data) {
    if (
      step === 'customer' &&
      (cart.data.email || cart.data.customerId) &&
      (cart.data.shipTo || cart.data.pickupBy || cart.data.carryOut)
    ) {
      step = 'shipping';
    }
    if (step === 'carrierPickupLocation') {
      step = 'shipping';
    }
    if (
      step === 'shipping' &&
      (!requiresShipping(cart.data) || cart.data.shipping.length > 0)
    ) {
      step = 'payment';
    }
    let pendingTenders;
    if (cart.data && cart.data.tenders) {
      pendingTenders = cart.data.tenders.find(
        (tender) => tender.chargeStatus === 'Pending'
      );
    }
    if (
      settings &&
      settings.data &&
      settings.data.payment.gateway !== 'PayGate'
    ) {
      if (step === 'payment' && (pendingTenders || cart.data.total === 0)) {
        step = 'complete';
      }
    }
  }
  return step;
};

export const requiresShipping = (cart) => {
  if (cart.orderDeliveryMethod === 'Pickup') {
    return false;
  }
  let shippingRequired = false;
  cart.items.forEach((item) => {
    if (item.hasShipping && item.type !== 'Bundle') {
      shippingRequired = true;
    }
    if (item.type === 'Bundle') {
      item.bundleItems.forEach((variant) => {
        if (variant.hasShipping) {
          shippingRequired = true;
        }
      });
    }
  });
  return shippingRequired;
};

export const requiresCarrierPickupLocation = (cart, settings) => {
  if (!requiresShipping(cart)) {
    return false;
  }
  if (!cart.shipTo || cart.shipTo.countryCode !== 'US') {
    return false;
  }
  if (!settings.data.shipCompliant.usesShipCompliant) {
    return false;
  }
  if (!settings.data.shipCompliant.usesCarrierPickupLocations) {
    return false;
  }
  return true;
};

export const stepArray = [
  'customer',
  'carrierPickupLocation',
  'shipping',
  'payment',
  'complete'
];

export const editStep = (cart, step) => {
  const currentStep = calculateStep(cart);
  const currentStepPosition = stepArray.indexOf(currentStep);
  const newStepPosition = stepArray.indexOf(step);
  if (currentStepPosition < newStepPosition) {
    return currentStep;
  }
  return step;
};

export const isChargable = (cart) => {
  if (chargeIssues(cart).length === 0) {
    return true;
  }

  return false;
};

const chargeIssues = (cart) => {
  const errorArray = [];

  // more than one order item and tender total === cartTotal
  const tenderTotal = cart.tenders.reduce((total, tender) => {
    if (tender.chargeStatus !== 'Failed') {
      return total + tender.amountTendered;
    }
    return total;
  }, 0);
  if (cart.items.length === 0) {
    errorArray.push('Cart must have one item in order to be charged.  ');
  }

  if (cart.tenders.length === 0 && cart.total !== 0) {
    errorArray.push(
      'Please enter a payment before you can charge this cart.  '
    );
  }

  if (
    tenderTotal !== cart.total &&
    cart.items.length > 0 &&
    cart.tenders.length > 0
  ) {
    errorArray.push(
      'Payment must equal cart total before you can charge this cart.  '
    );
  }

  if (!cart.shipTo && !cart.pickupBy && !cart.carryOut) {
    errorArray.push(
      'Cart requires a ship to address (or pickup) before this can be charged.  '
    );
  }

  if (cart.errors && cart.errors.length > 0) {
    errorArray.push(`${cart.errors[0].message}.  `);
  }

  return errorArray;
};

export const isCreditCardLoggedIn = (cart, customer) => {
  let showForm = false;
  if (customer && cart.customerId) {
    const giftCardTenderAmount = getAmountTenderedByGiftCard(cart);
    if (cart.total - giftCardTenderAmount > 0) {
      showForm = true;
    }
  }
  return showForm;
};

export const isCreditCardNotLoggedIn = (cart, customer) => {
  let showForm = false;
  if (!customer || !cart.customerId) {
    const giftCardTenderAmount = getAmountTenderedByGiftCard(cart);
    if (cart.total - giftCardTenderAmount > 0) {
      showForm = true;
    }
  }
  return showForm;
};

export const isBillAddressShipLoggedIn = (cart, customer) => {
  let showForm = false;
  if (customer && cart.orderDeliveryMethod === 'Ship') {
    showForm = true;
  }
  return showForm;
};

export const isBillAddressPickupLoggedIn = (cart, customer) => {
  let showForm = false;
  if (customer && cart.orderDeliveryMethod === 'Pickup') {
    showForm = true;
  }
  return showForm;
};

export const isCreditCardRequired = (cart) => {
  let showForm = false;
  const giftCardTenderAmount = getAmountTenderedByGiftCard(cart);
  if (cart.total - giftCardTenderAmount > 0) {
    showForm = true;
  }
  return showForm;
};

export const getDeliveryService = (service, carrier, services) => {
  if (!service || !carrier || !services) {
    return null;
  }

  const availableDeliveryDates = services.find(
    (shippingService) =>
      shippingService.service === service && shippingService.carrier === carrier
  );
  return availableDeliveryDates;
};

export const getDeliveryObject = (
  service,
  carrier,
  deliveryService,
  fastestEstimatedDelivery,
  requestedShipDate
) => {
  if (!service || !carrier || !deliveryService) {
    return null;
  }

  let deliveryObject;

  // If there is not requested ship date - use the fastest date available
  if (!requestedShipDate) {
    deliveryObject = fastestEstimatedDelivery.find(
      (shippingService) =>
        shippingService.service === service &&
        shippingService.carrier === carrier
    );
  }
  // Find the requested shipping object based on the date
  else {
    deliveryObject = deliveryService.estimatedDeliveryDates.find(
      (estimatedDeliveryDate) =>
        moment(estimatedDeliveryDate.requestedShipDate).format('YYYY-MM-DD') ===
        moment(requestedShipDate).format('YYYY-MM-DD')
    );
  }

  return deliveryObject;
};

export const getAmountDue = (cart) => {
  const tenderTotal = cart.tenders.reduce((total, tender) => {
    if (
      tender.chargeStatus !== 'Success' ||
      (tender.tenderType === 'Gift Card' && tender.chargeStatus !== 'Failed')
    ) {
      return total;
    }
    return total + tender.amountTendered;
  }, 0);
  return cart.total - tenderTotal;
};
